import { Modal, Tabs, message } from "antd";
import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getNominee, getSetting, updateNominee } from "../util/API";
import Nominators from "./Nominators";
import Summary from "./Summary";
import ButtonGroup from "./buttonGroup";
import NomineeInfoTab from "./nominee-tabs/NomineeInfoTab";
import ProofAttachmentTab from "./nominee-tabs/ProofAttachmentTab";
import ReferralEmailTab from "./nominee-tabs/ReferralEmailTab";
import ScienceTab from "./nominee-tabs/ScienceTab";
import ThesisTab from "./nominee-tabs/ThesisTab";
import { isStrEmpty } from "../util/helper";
import { red } from "@ant-design/colors";

const NomineeInfo = ({ userInfo, isSecretariatView = false }) => {
  const { t } = useTranslation();
  const [dataForUpdate, setDataForUpdate] = useState();

  //#region Button group
  const [activeKey, setActiveKey] = useState("1");
  const [isShowButtonGrp, setIsShowButtonGrp] = useState(false);
  const [isFirstForm, setIsFirstForm] = useState(false);
  const [isLastForm, setIsLastForm] = useState(false);
  const [disabled, setDisabled] = useState(false);
  //#endregion

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSummaryModal, setShowSummaryModal] = useState(false);

  const [closeDate, setCloseDate] = useState("2023-12-20");

  const [messageApi, contextHolder] = message.useMessage();

  const [changeEmailConfirmed, setChangeEmailConfirmed] = useState(false);
  const [showConfirmChangeEmailModal, setShowConfirmChangeEmailModal] =
    useState(false);

  const rankingData = [
    {
      key: "school_rank",
      domestic: "",
      international: "",
      organization_rank: "",
      note: "",
    },
    {
      key: "major_rank",
      domestic: "",
      international: "",
      organization_rank: "",
      note: "",
    },
  ];

  const prizeData = [
    {
      key: "orcid",
      name: "str_orcid_identifier_index",
      url: "",
    },
    {
      key: "google_scholar",
      name: "str_google_scholar_profile",
      url: "",
    },
    {
      key: "other",
      name: "str_other",
      url: "",
    },
  ];

  useEffect(() => {
    if (!isSecretariatView) getSettingInfo();
  }, []);

  useEffect(() => {
    if (dataForUpdate) {
      if (!dataForUpdate?.thesis?.school_rankings) {
        setDataForUpdate({
          ...dataForUpdate,
          thesis: { ...dataForUpdate.thesis, school_rankings: rankingData },
        });
      }

      if (!dataForUpdate?.thesis?.prizes_v2) {
        setDataForUpdate({
          ...dataForUpdate,
          thesis: { ...dataForUpdate.thesis, prizes_v2: prizeData },
        });
      }

      if (!dataForUpdate?.instructor_detail) {
        setDataForUpdate({ ...dataForUpdate, instructor_detail: prizeData });
      }
    }
  }, [dataForUpdate]);

  useEffect(() => {
    const getNomineeInfo = async (api_path) => {
      try {
        const response = await getNominee(api_path);
        setDataForUpdate(response.data);
      } catch (error) {
        console.error(error);
        // TODO: Handle error while fetching profile
      }
    };

    const api_path = userInfo?.api_path ?? "/api/nominatees/" + userInfo?.id;
    getNomineeInfo(api_path);
  }, [userInfo?.api_path, userInfo?.id]);

  if (!userInfo) {
    return <div>Loading...</div>;
  }

  const handleSubmit = async () => {
    const checkValid = isValid();
    if (!checkValid) {
      return;
    }

    handleSave();
    setShowConfirmModal(true);
  };

  const handleSave = async () => {
    setDisabled(true);
    //* Check if email changed
    if (!changeEmailConfirmed && isEmailChanged()) {
      setShowConfirmChangeEmailModal(true);
      setDisabled(false);
      return;
    }

    if (dataForUpdate?.createdBy) {
      delete dataForUpdate.createdBy;
    }
    if (dataForUpdate?.updatedBy) {
      delete dataForUpdate.updatedBy;
    }

    try {
      const res = await updateNominee(userInfo.api_path, dataForUpdate);
      if (res.status === 200) {
        setDataForUpdate(res.data);
        setDisabled(false);
        messageApi.open({
          type: "success",
          content: `${t("str_save_success")}`,
        });
        return;
      }
    } catch (error) {}
  };

  const handleChangeTab = (key) => {
    setActiveKey(key);

    switch (key) {
      case "1":
        setIsShowButtonGrp(false);
        setIsFirstForm(false);
        setIsLastForm(false);
        break;
      case "2":
        setIsShowButtonGrp(true);
        setIsFirstForm(true);
        setIsLastForm(false);
        break;
      case tabItems.length.toString():
        setIsShowButtonGrp(true);
        setIsFirstForm(false);
        setIsLastForm(true);
        break;
      default:
        setIsShowButtonGrp(true);
        setIsFirstForm(false);
        setIsLastForm(false);
        break;
    }
  };

  const handleNextButtonChange = () => {
    handleSave(true);

    const key = (parseInt(activeKey) + 1).toString();
    handleChangeTab(key);
  };

  const handlePrevButtonChange = () => {
    handleSave(true);

    const key = (parseInt(activeKey) - 1).toString();
    handleChangeTab(key);
  };

  const isValid = () => {
    //* CANDIDATE'S INFORMATION TAB
    const requiredFieldsTab2 = [
      { key: "name", message: t("str_required_full_name") },
      { key: "dob", message: t("str_required_dob") },
      { key: "gender", message: t("str_required_gender") },
      { key: "place_of_birth", message: t("str_required_place_of_birth") },
      { key: "nationality", message: t("str_required_nationality") },
      { key: "address", message: t("str_required_address") },
      { key: "phone", message: t("str_required_phone_number") },
      { key: "email", message: t("str_required_email") },
      { key: "degree", message: t("str_required_academic_rank_and_degree") },
      { key: "position", message: t("str_required_position") },
      {
        key: "address_working_unit",
        message: t("str_required_working_place_address"),
      },
      {
        key: "web_working_unit",
        message: t("str_required_working_place_website"),
      },
    ];

    for (const field of requiredFieldsTab2) {
      if (isStrEmpty(dataForUpdate?.[field.key])) {
        messageApi.open({
          type: "error",
          content: field.message,
        });
        handleSave(true);
        handleChangeTab("2");
        return false;
      }
    }

    //* THESIS INFORMATION TAB
    const requiredFieldsTab3 = [
      {
        path: ["school"],
        message: t("str_required_academic_institution_name"),
      },
      {
        path: ["school_address"],
        message: t("str_required_academic_institution_address"),
      },
      {
        path: ["school_website"],
        message: t("str_required_academic_institution_website"),
      },

      { path: ["thesis", "name"], message: t("str_required_thesis_title") },
      { path: ["thesis", "specialization"], message: t("str_required_major") },
      {
        path: ["thesis", "year_of_protection"],
        message: t("str_required_year_of_thesis_defense"),
      },
      {
        path: ["thesis", "summary"],
        message: t("str_upload") + t("str_nominated_thesis_information_new"),
      },

      {
        path: ["instructor_name"],
        message: t("str_required_full_name_instructor"),
      },
      {
        path: ["instructor_degree"],
        message: t("str_required_academic_rank_and_degree_instructor"),
      },
      {
        path: ["instructor_working_unit"],
        message: t("str_required_working_place_instructor"),
      },
      {
        path: ["instructor_email"],
        message: t("str_required_email_instructor"),
      },
      {
        path: ["instructor_phone"],
        message: t("str_required_phone_number_instructor"),
      },
      {
        path: ["instructor_address"],
        message: t("str_required_contact_address_instructor"),
      },
    ];

    function getValue(obj, path) {
      return path.reduce((acc, key) => acc?.[key], obj);
    }

    for (const field of requiredFieldsTab3) {
      const value = getValue(dataForUpdate, field.path);
      if (isStrEmpty(value)) {
        messageApi.open({
          type: "error",
          content: field.message,
        });
        handleSave(true);
        handleChangeTab("3");
        return false;
      }
    }

    //* OPTIONAL TABLE FIELDS INFO
    //if (
    //  !dataForUpdate?.science_missions ||
    //  dataForUpdate?.science_missions?.length === 0
    //) {
    //  messageApi.open({
    //    type: "error",
    //    content: t("str_upload") + t("str_nominated_thesis_information_new"),
    //  });
    //  return false;
    //}

    //* SCIENTIFIC ACHIEVEMENTS AND WORKS
    if (
      !dataForUpdate?.scientific_articles ||
      dataForUpdate?.scientific_articles?.length === 0
    ) {
      messageApi.open({
        type: "error",
        content: t("str_fill") + t("str_scientific_journals"),
      });
      handleSave(true);
      handleChangeTab("4");
      return false;
    }
    if (!dataForUpdate?.background || dataForUpdate?.background?.length === 0) {
      messageApi.open({
        type: "error",
        content: t("str_upload") + t("str_nominee_scientific_cv"),
      });
      handleSave(true);
      handleChangeTab("4");
      return false;
    }
    if (
      !dataForUpdate?.certificate_files ||
      dataForUpdate?.certificate_files?.length === 0
    ) {
      messageApi.open({
        type: "error",
        content: t("str_upload") + t("str_degree_certificate"),
      });
      handleSave(true);
      handleChangeTab("4");
      return false;
    }

    //* REFERRAL EMAILS TAB
    if (
      !dataForUpdate?.referral_emails ||
      dataForUpdate?.referral_emails?.length === 0 ||
      dataForUpdate?.referral_emails[0]?.content === null
    ) {
      messageApi.open({
        type: "error",
        content: t("str_please_upload_first_letter"),
      });
      handleSave(true);
      handleChangeTab("5");
      return false;
    }

    //* PROOFS OF ORIGIN
    const validations = [
      {
        condition: !dataForUpdate?.type_proof,
        message: t("str_required_select_nationalily_or_ethnic_background"),
      },
      {
        condition: !dataForUpdate?.identifier_type,
        message: t("str_required_select_type_of_proof"),
      },
      {
        condition:
          dataForUpdate?.selected_required_attachment === "birth_certificate" &&
          !dataForUpdate?.proof_attachment,
        message: t("str_required_attach_proof_relationship"),
      },
      {
        condition: !dataForUpdate?.identifier_attachment,
        message: t("str_required_attach_proof"),
      },
    ];

    for (const { condition, message } of validations) {
      if (condition) {
        messageApi.open({
          type: "error",
          content: message,
        });
        handleSave(true);
        handleChangeTab("6");
        return false;
      }
    }

    // if (
    //   !dataForUpdate?.invention_awards ||
    //   dataForUpdate?.invention_awards?.length === 0
    // )
    //   return false;
    // if (!dataForUpdate?.diplomas || dataForUpdate?.diplomas?.length === 0)
    //   return false;
    // if (
    //   !dataForUpdate?.commercialization_missions ||
    //   dataForUpdate?.commercialization_missions?.length === 0
    // )
    //   return false;

    // if (!dataForUpdate?.monographs || dataForUpdate?.monographs?.length === 0)
    //   return false;

    // if (
    //   !dataForUpdate?.thesis?.prizes_v2 ||
    //   dataForUpdate?.thesis?.prizes_v2?.length === 0
    // )
    //   return false;

    // if (
    //   !dataForUpdate?.thesis?.members ||
    //   dataForUpdate?.thesis?.members?.length === 0
    // )
    //   return false;

    return true;
  };

  const isEmailChanged = () => {
    if (dataForUpdate?.email !== userInfo?.email) return true;
    return false;
  };

  const viewSummary = () => {
    setShowConfirmModal(false);
    setShowSummaryModal(true);
  };

  const submitForm = async () => {
    try {
      const response = await updateNominee(userInfo.api_path, {
        ...dataForUpdate,
        isFinalSubmit: true,
      });
      if (response.status === 200) {
        messageApi.open({
          type: "success",
          content: (
            <p>
              {t("str_submit_cv_success")} <br />
              {t("str_submit_cv_confirm", {
                date: dayjs(closeDate).format("DD/MM/YYYY"),
              })}
            </p>
          ),
          duration: 5,
        });

        setShowSummaryModal(false);
      }
      return;
    } catch (error) {
      messageApi.open({
        type: "error",
        content: `${t("str_save_failed")}`,
        duration: 5,
      });
    }
  };

  const getSettingInfo = async () => {
    try {
      const response = await getSetting();
      if (response.status === 200) {
        const data = response.data?.data;
        const item = data.find(
          (x) => x.attributes.name === "close_nominations",
        );

        if (item) {
          if (
            item?.attributes?.value?.close_at !== null &&
            item?.attributes?.value?.close_at !== ""
          )
            setCloseDate(item?.attributes?.value?.close_at);
        }
      }
    } catch (err) {}
  };

  const handleConfirmedChangeEmail = async () => {
    setChangeEmailConfirmed(true);

    try {
      const res = await updateNominee(userInfo.api_path, dataForUpdate);

      if (res.status === 200) {
        setDataForUpdate(res.data);
        messageApi.open({
          type: "success",
          content: `${t("str_save_success")}`,
        });
        setShowConfirmChangeEmailModal(false);
        return;
      }
    } catch (error) {}
  };

  const handleCancelChangeEmail = () => {
    setDataForUpdate({ ...dataForUpdate, email: userInfo?.email });
    setShowConfirmChangeEmailModal(false);
  };

  const tabItems = [
    {
      key: "1",
      label: `${t("str_nominator_info")}`,
      children: (
        <Nominators
          data={dataForUpdate?.nominators}
          disabled={true}
          isSecretariatView={isSecretariatView}
        />
      ),
    },
    {
      key: "2",
      label: `${t("str_nominee_info")}`,
      children: (
        <NomineeInfoTab
          dataForUpdate={dataForUpdate}
          setDataForUpdate={setDataForUpdate}
          isSecretariatView={isSecretariatView}
        />
      ),
    },
    {
      key: "3",
      label: `${t("str_thesis_information")}`,
      children: (
        <ThesisTab
          dataForUpdate={dataForUpdate}
          setDataForUpdate={setDataForUpdate}
          isSecretariatView={isSecretariatView}
        />
      ),
    },
    {
      key: "4",
      label: `${t("str_inventions")}`,
      children: (
        <ScienceTab
          dataForUpdate={dataForUpdate}
          setDataForUpdate={setDataForUpdate}
          isSecretariatView={isSecretariatView}
        />
      ),
    },
    {
      key: "5",
      label: <span>{t("str_letter_recommendation")}</span>,
      label: (
        <span>
          {t("str_letter_recommendation")}
          <span style={{ color: "red", marginLeft: 4 }}>*</span>
        </span>
      ),
      children: (
        <ReferralEmailTab
          dataForUpdate={dataForUpdate}
          setDataForUpdate={setDataForUpdate}
          isSecretariatView={isSecretariatView}
        />
      ),
    },
    {
      key: "6",
      label: <span>{t("str_documentation_origin")}</span>,
      label: (
        <span>
          {t("str_documentation_origin")}
          <span style={{ color: "red", marginLeft: 4 }}>*</span>
        </span>
      ),
      children: (
        <ProofAttachmentTab
          dataForUpdate={dataForUpdate}
          setDataForUpdate={setDataForUpdate}
          isSecretariatView={isSecretariatView}
        />
      ),
    },
  ];

  return (
    <div>
      {contextHolder}
      <div>
        <Tabs
          defaultActiveKey="1"
          items={tabItems}
          tabPosition="top"
          animated={false}
          onChange={handleChangeTab}
          activeKey={activeKey}
        />
      </div>
      <br />

      <ButtonGroup
        isShow={!isSecretariatView && isShowButtonGrp}
        isFirst={isFirstForm}
        isLast={isLastForm}
        onSave={() => handleSave(false)}
        onNext={handleNextButtonChange}
        onPrev={handlePrevButtonChange}
        onSubmit={handleSubmit}
        disabled={disabled}
      />

      <Modal
        open={showConfirmModal}
        onOk={viewSummary}
        onCancel={() => setShowConfirmModal(false)}
      >
        <p>
          {t("str_review_information")} <br />
          {t("str_submit_cv_confirm", {
            date: dayjs(closeDate).format("DD/MM/YYYY"),
          })}
        </p>
      </Modal>

      <Modal
        title=""
        centered
        open={showSummaryModal}
        onOk={submitForm}
        onCancel={() => setShowSummaryModal(false)}
        zIndex={4}
        width={1000}
        okText={t("str_submit")}
        cancelText={t("str_cancel")}
        destroyOnClose
      >
        <Summary data={dataForUpdate} />
      </Modal>

      <Modal
        title={t("str_email_change_confirm")}
        centered
        open={showConfirmChangeEmailModal}
        onOk={handleConfirmedChangeEmail}
        onCancel={handleCancelChangeEmail}
        okText={t("str_confirm")}
        cancelText={t("str_cancel")}
      >
        <p>
          {t("str_email_change_confirm_str1")}{" "}
          <span style={{ color: red[7], fontWeight: "500" }}>
            {userInfo?.email}
          </span>{" "}
          {t("str_email_change_confirm_str2")}{" "}
          <span style={{ color: red[7], fontWeight: "500" }}>
            {dataForUpdate?.email}
          </span>{" "}
          .
        </p>
      </Modal>
    </div>
  );
};

export default NomineeInfo;
